import React from 'react';
import { Box, Container, Typography, Grid, Paper, Button, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SectionHeading from '../components/SectionHeading';
import FounderSection from '../components/FounderSection';
import { gradients } from '../theme/theme';
import FlagIcon from '@mui/icons-material/Flag';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CategoryIcon from '@mui/icons-material/Category';
import HandshakeIcon from '@mui/icons-material/Handshake';
import StarIcon from '@mui/icons-material/Star';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import GroupsIcon from '@mui/icons-material/Groups';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const AboutPage = () => {
  const { t } = useTranslation();

  const cards = [
    { icon: FlagIcon, title: t('about.mission.title'), description: t('about.mission.description'), color: '#1565C0' },
    { icon: VisibilityIcon, title: t('about.vision.title'), description: t('about.vision.description'), color: '#00897B' },
    { icon: CategoryIcon, title: t('about.areas.title'), description: t('about.areas.description'), color: '#5C6BC0' },
  ];

  const stats = [
    { ...t('home.stats.experience', { returnObjects: true }) },
    { ...t('home.stats.sectors', { returnObjects: true }) },
    { ...t('home.stats.support', { returnObjects: true }) },
    { ...t('home.stats.focus', { returnObjects: true }) },
  ];

  const values = [
    { icon: HandshakeIcon, key: 'trust', color: '#1565C0' },
    { icon: StarIcon, key: 'quality', color: '#00897B' },
    { icon: LightbulbIcon, key: 'innovation', color: '#FF7043' },
    { icon: GroupsIcon, key: 'customer', color: '#5C6BC0' },
  ];

  const facts = [
    { icon: CalendarMonthIcon, label: t('about.fact1') },
    { icon: VerifiedUserIcon, label: t('about.fact2') },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Intro — two columns */}
      <Box sx={{ pt: { xs: 12, md: 16 }, pb: { xs: 7, md: 11 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 7 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Typography variant="overline" sx={{ color: 'primary.main', mb: 1.5, display: 'block' }}>
                  {t('about.eyebrow')}
                </Typography>
                <Typography variant="h2" sx={{ mb: 2.5, color: 'text.primary' }}>
                  {t('about.title')}
                </Typography>
                <Box sx={{ width: 64, height: 4, borderRadius: 2, background: 'linear-gradient(90deg,#1565C0,#FF7A45)', mb: 3 }} />
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, fontSize: '1.08rem', lineHeight: 1.85 }}>
                  {t('about.description')}
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 1.25 }}>
                  {facts.map((fact, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 999,
                        bgcolor: 'rgba(21,101,192,0.07)',
                        border: '1px solid rgba(21,101,192,0.18)',
                      }}
                    >
                      <fact.icon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: 'text.primary' }}>
                        {fact.label}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box sx={{ position: 'relative', pl: { md: 3 }, pb: { xs: 4, md: 3 } }}>
                  <Box
                    component="img"
                    src="/about/team-7526590.jpg"
                    alt={t('about.title')}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      borderRadius: 5,
                      display: 'block',
                      boxShadow: '0 40px 80px -40px rgba(13,71,161,0.55)',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: { xs: 0, md: 12 },
                      left: { xs: 8, md: -8 },
                      bgcolor: 'background.paper',
                      borderRadius: 4,
                      px: 3,
                      py: 1.75,
                      textAlign: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 24px 50px -24px rgba(16,32,50,0.45)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Plus Jakarta Sans", sans-serif',
                        fontWeight: 800,
                        fontSize: '2rem',
                        lineHeight: 1,
                        color: 'primary.main',
                      }}
                    >
                      2022
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {t('home.about.badgeLabel')}
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <FounderSection />

      {/* Mission / Vision / Areas */}
      <Box sx={{ py: { xs: 7, md: 11 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow={t('about.purposeEyebrow')}
            title={t('about.purposeTitle')}
            sx={{ mb: 7 }}
          />
          <Grid container spacing={4}>
            {cards.map((card, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  style={{ height: '100%' }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: 4,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.35s ease',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${card.color}, ${card.color}55)`,
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.4s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 26px 50px -28px ${card.color}aa`,
                        borderColor: `${card.color}55`,
                      },
                      '&:hover::before': { transform: 'scaleX(1)' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 68,
                        height: 68,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${card.color}, ${card.color}cc)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        boxShadow: `0 14px 28px -12px ${card.color}99`,
                      }}
                    >
                      <card.icon sx={{ fontSize: 34, color: 'white' }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                      {card.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
                      {card.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Stats Band */}
      <Box sx={{ position: 'relative', py: { xs: 6, md: 8 }, background: gradients.hero, overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.5,
            background:
              'radial-gradient(circle at 15% 20%, rgba(255,255,255,0.12), transparent 40%), radial-gradient(circle at 85% 80%, rgba(255,122,69,0.18), transparent 45%)',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Grid container spacing={{ xs: 3, md: 2 }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Box sx={{ textAlign: 'center', color: 'white' }}>
                    <Typography
                      sx={{
                        fontFamily: '"Plus Jakarta Sans", sans-serif',
                        fontWeight: 800,
                        fontSize: { xs: '2.2rem', md: '3rem' },
                        lineHeight: 1,
                        mb: 1,
                        background: 'linear-gradient(135deg, #ffffff, #cfe3ff)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontWeight: 500, fontSize: { xs: '0.85rem', md: '0.95rem' } }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Values */}
      <Box sx={{ py: { xs: 7, md: 11 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow={t('about.values.eyebrow')}
            title={t('about.values.title')}
            sx={{ mb: 7 }}
          />
          <Grid container spacing={4}>
            {values.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={item.key}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{ height: '100%' }}
                >
                  <Box sx={{ textAlign: 'center', height: '100%', px: 2 }}>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${item.color}1A, ${item.color}33)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2.5,
                      }}
                    >
                      <item.icon sx={{ fontSize: 32, color: item.color }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                      {t(`about.values.items.${item.key}.title`)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                      {t(`about.values.items.${item.key}.description`)}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Band */}
      <Box sx={{ py: { xs: 7, md: 9 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 6,
                px: { xs: 3, md: 8 },
                py: { xs: 5, md: 7 },
                background: gradients.primary,
                boxShadow: '0 30px 60px -30px rgba(13,71,161,0.6)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.4,
                  background: 'radial-gradient(circle at 85% 15%, rgba(255,122,69,0.35), transparent 40%)',
                }}
              />
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={4}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                sx={{ position: 'relative' }}
              >
                <Box sx={{ maxWidth: 640 }}>
                  <Typography variant="h3" sx={{ color: 'white', mb: 1.5 }}>
                    {t('about.cta.title')}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontSize: '1.05rem', lineHeight: 1.7 }}>
                    {t('about.cta.desc')}
                  </Typography>
                </Box>
                <Button
                  component={Link}
                  to="/iletisim"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    flexShrink: 0,
                    bgcolor: 'white',
                    color: 'primary.dark',
                    px: 4,
                    py: 1.6,
                    fontSize: '1rem',
                    fontWeight: 700,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)', transform: 'translateY(-2px)' },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {t('about.cta.button')}
                </Button>
              </Stack>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage;
